import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Spin,
  message,
  Divider,
  FloatButton,
} from "antd";
import { LeftOutlined, RightOutlined, UpOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Home.css";
import {
  SmileTwoTone,
  FireTwoTone,
  GiftTwoTone,
  StarTwoTone,
  HeartTwoTone,
  ThunderboltTwoTone,
  CrownOutlined,
  CheckCircleTwoTone,
  CustomerServiceTwoTone,
  ToolTwoTone,
} from "@ant-design/icons";

const { Meta } = Card;
const { Title, Paragraph } = Typography;

const Home = () => {
  const [startIndexTrending, setStartIndexTrending] = useState(0);
  const [startIndexNewArrival, setStartIndexNewArrival] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);
  const sliderRefTrending = useRef(null);
  const sliderRefNewArrival = useRef(null);

  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);

  const [newArrivalApiData, setNewArrivalApiData] = useState([]);
  const [loadingNewArrival, setLoadingNewArrival] = useState(true);
  const [trendingApiData, setTrendingApiData] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(true);
  const navigate = useNavigate();

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      setCatLoading(true);
      try {
        const tokenString = localStorage.getItem("token");
        const response = await axios.get(
          global.config.APIURL.BaseURL +
            "api/ProductCategory/ListofProductCategory",
          {
            headers: {
              Authorization: "Bearer " + tokenString,
            },
          }
        );
        const allCategories = response.data.flatMap((cat) => cat.data);
        setCategories(allCategories);
      } catch (error) {
        message.error("Failed to load categories.");
        setCategories([]);
      } finally {
        setCatLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch New Arrival data
  useEffect(() => {
    const fetchNewArrivals = async () => {
      setLoadingNewArrival(true);
      try {
        const tokenString = localStorage.getItem("token");
        const response = await axios.get(
          global.config.APIURL.BaseURL + "api/NewArrival/GetNewArrivals",
          {
            headers: {
              Authorization: "Bearer " + tokenString,
            },
          }
        );
        setNewArrivalApiData(response.data || []);
      } catch (error) {
        setNewArrivalApiData([]);
      } finally {
        setLoadingNewArrival(false);
      }
    };
    fetchNewArrivals();
  }, []);

  // Fetch Trending Items data
  useEffect(() => {
    const fetchTrendingItems = async () => {
      setLoadingTrending(true);
      try {
        const tokenString = localStorage.getItem("token");
        const response = await axios.get(
          global.config.APIURL.BaseURL + "api/TrendingItems/GetTrendingItems",
          {
            headers: {
              Authorization: "Bearer " + tokenString,
            },
          }
        );
        setTrendingApiData(response.data || []);
      } catch (error) {
        setTrendingApiData([]);
      } finally {
        setLoadingTrending(false);
      }
    };
    fetchTrendingItems();
  }, []);

  // Prepare 1 product from each category for the slider
  const newArrivalSliderData = newArrivalApiData
    .map((cat) =>
      cat.Products && cat.Products.length > 0
        ? { ...cat.Products[0], Category: cat.Category }
        : null
    )
    .filter(Boolean);

  // Prepare 1 product from each trending category for the slider
  const trendingSliderData = trendingApiData
    .map((cat) =>
      cat.Products && cat.Products.length > 0
        ? { ...cat.Products[0], Category: cat.Category }
        : null
    )
    .filter(Boolean);

  // Responsive visible count
  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth <= 768) {
        setVisibleCount(1);
      } else {
        setVisibleCount(4);
      }
    };
    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);
    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);

  // Trending slider auto
  useEffect(() => {
    const interval = setInterval(() => {
      setStartIndexTrending((prev) =>
        prev + 1 < 7 - visibleCount + 1 ? prev + 1 : 0
      );
    }, 3500);
    return () => clearInterval(interval);
  }, [visibleCount]);

  // New Arrival slider auto
  useEffect(() => {
    const interval = setInterval(() => {
      setStartIndexNewArrival((prev) =>
        prev + 1 < newArrivalSliderData.length - visibleCount + 1 ? prev + 1 : 0
      );
    }, 3500);
    return () => clearInterval(interval);
  }, [visibleCount, newArrivalSliderData.length]);

  // Touch slider handlers
  const handleTouchStart = (e, sliderRef) => {
    sliderRef.current.startX = e.touches[0].clientX;
  };
  const handleTouchMove = (e, sliderRef, handlePrev, handleNext) => {
    if (!sliderRef.current.startX) return;
    const diffX = e.touches[0].clientX - sliderRef.current.startX;
    if (diffX > 50) {
      handlePrev();
      sliderRef.current.startX = null;
    } else if (diffX < -50) {
      handleNext();
      sliderRef.current.startX = null;
    }
  };

  // Slider navigation
  const handlePrevTrending = () =>
    setStartIndexTrending((prev) => Math.max(prev - 1, 0));
  const handleNextTrending = () =>
    setStartIndexTrending((prev) => Math.min(prev + 1, 7 - visibleCount));
  const handlePrevNewArrival = () =>
    setStartIndexNewArrival((prev) => Math.max(prev - 1, 0));
  const handleNextNewArrival = () =>
    setStartIndexNewArrival((prev) =>
      Math.min(prev + 1, newArrivalSliderData.length - visibleCount)
    );

  // Minimal Card Style
  const minimalCardStyle = {
    borderRadius: 0,
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    border: "1px solid #f0f0f0",
    background: "#fff",
    transition: "box-shadow 0.2s",
  };

  // Minimal Section Card
  const sectionCardStyle = {
    background: "#fff",
    borderRadius: 0,
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    padding: "32px 16px",
    marginBottom: 32,
    border: "1px solid #f0f0f0",
    borderRadius: 24,
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #f7f9fb 60%, #fff0f6 100%)",
        minHeight: "100vh",
        padding: "32px 0",
      }}
    >
      {/* Hero Section */}
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto 40px auto",
          padding: "32px 24px",
          background: "linear-gradient(90deg, #fff 60%, #e6f7ff 100%)",
          borderRadius: 24,
          boxShadow: "0 6px 32px rgba(0,0,0,0.08)",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 32,
        }}
      >
        <div style={{ flex: 1, minWidth: 260 }}>
          <Title
            level={1}
            style={{ fontWeight: 800, color: "#1677ff", marginBottom: 8 }}
          >
            Welcome to Jewels of Joy (JOJ)
          </Title>
          <Paragraph style={{ fontSize: 18, color: "#444", marginBottom: 24 }}>
            Explore trending wholesale jewellery, view our newest arrivals, and
            access a wide selection of categories all at unbeatable wholesale
            rates. <br />
            <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
              Quality. Style. Trust.
            </span>
          </Paragraph>
          <Button
            type="primary"
            size="large"
            style={{
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 18,
              minWidth: 180,
              marginRight: 16,
              background: "#1677ff",
              borderColor: "#1677ff",
            }}
            onClick={() => navigate("/newarrival")}
          >
            <ThunderboltTwoTone
              twoToneColor="#fff700"
              style={{ marginRight: 8 }}
            />
            Shop New Arrivals
          </Button>
          <Button
            type="default"
            size="large"
            style={{
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 18,
              minWidth: 180,
              borderColor: "#ff4d4f",
              color: "#ff4d4f",
              marginTop: 8,
            }}
            onClick={() => navigate("/trendingitems")}
          >
            <FireTwoTone twoToneColor="#ff4d4f" style={{ marginRight: 8 }} />
            Explore Trending
          </Button>
        </div>
        <div style={{ flex: 1, minWidth: 260, textAlign: "center" }}>
          <div
            className="joj-logo-desktop"
            style={{
              width: "80%",
              maxWidth: 320,
              minWidth: 180,
              borderRadius: 16,
              boxShadow: "0 4px 24px rgba(22,119,255,0.08)",
              padding: 16,
              display: "inline-block",
            }}
          >
            <img
              src="https://thejoj.com/images/LOGORcopyColor.png"
              alt="JOJ Logo"
              style={{
                width: "100%",
                display: "block",
                borderRadius: 12,
                background: "transparent",
              }}
            />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto 40px auto",
          padding: "24px 0",
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          justifyContent: "center",
        }}
      >
        {[
          {
            icon: (
              <CrownOutlined
                style={{ fontSize: 40, marginBottom: 12, color: "#1677ff" }}
              />
            ),
            title: "Exclusive Wholesale Designs",
            desc: "Access a curated selection of unique jewellery styles, available only for our wholesale partners.",
            bg: "#f0f5ff",
          },
          {
            icon: (
              <CheckCircleTwoTone
                twoToneColor="#52c41a"
                style={{ fontSize: 40, marginBottom: 12 }}
              />
            ),
            title: "Unmatched Quality",
            desc: "Count on us for consistently superior craftsmanship and materials, trusted by retailers nationwide.",
            bg: "#fffbe6",
          },
          {
            icon: (
              <CustomerServiceTwoTone
                twoToneColor="#ff4d4f"
                style={{ fontSize: 40, marginBottom: 12 }}
              />
            ),
            title: "Dedicated B2B Support",
            desc: "Enjoy personalized service, easy bulk ordering, and responsive assistance for all your wholesale needs.",
            bg: "#fff0f6",
          },
          {
            icon: (
              <ToolTwoTone
                twoToneColor="#faad14"
                style={{ fontSize: 40, marginBottom: 12 }}
              />
            ),
            title: "Custom-Made for You",
            desc: "Get jewellery tailored to your unique requirements—share your designs or ideas, and we’ll craft them to match your business needs.",
            bg: "#e6fffb",
          },
        ].map((card, idx) => (
          <Card
            key={idx}
            hoverable
            style={{
              minWidth: 220,
              maxWidth: "90%",
              width: "100%",
              borderRadius: 16,
              textAlign: "center",
              background: card.bg,
              border: "none",
              flex: "1 1 220px",
            }}
            className="feature-card"
          >
            {card.icon}
            <Title level={4} style={{ margin: 0 }}>
              {card.title}
            </Title>
            <Paragraph style={{ color: "#555" }}>{card.desc}</Paragraph>
          </Card>
        ))}
      </div>

      {/* Categories */}
      <Card style={sectionCardStyle} styles={{ padding: 0 }}>
        <Title
          level={3}
          style={{
            textAlign: "center",
            marginBottom: 32,
            fontWeight: 700,
            letterSpacing: 1,
          }}
        >
          Our Categories
        </Title>
        {catLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Row gutter={[24, 24]} justify="center">
            {categories.slice(0, 4).map((category) => (
              <Col key={category.ProductCategoryID} xs={24} sm={12} md={6}>
                <Card
                  hoverable
                  style={{
                    ...minimalCardStyle,
                    padding: 0,
                    textAlign: "center",
                    height: 240, // Increased height for better image coverage
                    borderRadius: 10,
                  }}
                  cover={
                    <img
                      alt={category.CategoryName}
                      src={category.CategoryImage}
                      style={{
                        height: 180, // Increased from 140 to 180
                        objectFit: "cover",
                        borderRadius: 0,
                        borderBottom: "1px solid #f0f0f0",
                        borderRadius: 10,
                      }}
                    />
                  }
                  onClick={() => navigate("/productcategories")}
                >
                  <Meta
                    title={
                      <span style={{ fontWeight: 600, fontSize: 16 }}>
                        {category.CategoryName}
                      </span>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
        <div style={{ textAlign: "center", marginTop: 32 }}>
          <Button
            type="primary"
            size="large"
            style={{
              borderRadius: 0,
              fontWeight: 600,
              letterSpacing: 1,
              minWidth: 220,
            }}
            onClick={() => navigate("/productcategories")}
          >
            Explore All Categories
          </Button>
        </div>
      </Card>

      {/* Trending Items */}
      <Card style={sectionCardStyle} styles={{ padding: 0 }}>
        <Title
          level={3}
          style={{
            textAlign: "center",
            marginBottom: 32,
            fontWeight: 700,
            letterSpacing: 1,
          }}
        >
          Trending Items
        </Title>
        {loadingTrending ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div
              className="slider-container"
              ref={sliderRefTrending}
              onTouchStart={(e) => handleTouchStart(e, sliderRefTrending)}
              onTouchMove={(e) =>
                handleTouchMove(
                  e,
                  sliderRefTrending,
                  handlePrevTrending,
                  handleNextTrending
                )
              }
            >
              <Button
                icon={<LeftOutlined />}
                onClick={handlePrevTrending}
                disabled={startIndexTrending === 0}
                className="slider-arrow left-arrow"
                style={{ border: "none", background: "transparent" }}
              />
              <div className="card-container" style={{ width: "100%" }}>
                <div
                  className="card-slider"
                  style={{
                    transform: `translateX(-${
                      startIndexTrending * (100 / visibleCount)
                    }%)`,
                    transition: "transform 0.5s cubic-bezier(.4,0,.2,1)",
                    display: "flex",
                  }}
                >
                  {trendingSliderData.map((item, index) => (
                    <div
                      key={item.ProductID || index}
                      style={{
                        flex: `0 0 ${100 / visibleCount}%`,
                        padding: "0 12px",
                      }}
                    >
                      <Card
                        hoverable
                        style={{
                          ...minimalCardStyle,
                          width: "100%",
                          height: 380,
                          padding: 0,
                          borderRadius: 10,
                        }}
                        cover={
                          <img
                            alt={item.ProductName}
                            src={item.ImageUrl}
                            style={{
                              height: 240,
                              objectFit: "cover",
                              borderRadius: 0,
                              borderBottom: "1px solid #f0f0f0",
                              borderRadius: 10,
                            }}
                          />
                        }
                        onClick={() => navigate("/trendingitems")}
                      >
                        <Meta
                          title={
                            <span style={{ fontWeight: 600, fontSize: 15 }}>
                              {item.ProductName}
                            </span>
                          }
                          description={
                            <div
                              style={{
                                fontSize: 13,
                                color: "#888",
                                minHeight: 30,
                              }}
                            >
                              <div>{item.Category}</div>
                              <div>₹{item.SalesRate?.toFixed(2) || ""}</div>
                            </div>
                          }
                        />
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
              <Button
                icon={<RightOutlined />}
                onClick={handleNextTrending}
                disabled={
                  startIndexTrending + visibleCount >= trendingSliderData.length
                }
                className="slider-arrow right-arrow"
                style={{ border: "none", background: "transparent" }}
              />
            </div>
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <Button
                type="primary"
                size="large"
                style={{
                  borderRadius: 0,
                  fontWeight: 600,
                  letterSpacing: 1,
                  minWidth: 220,
                }}
                onClick={() => navigate("/trendingitems")}
              >
                Show All Trending Items
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* New Arrival */}
      <Card style={sectionCardStyle} styles={{ padding: 0 }}>
        <Title
          level={3}
          style={{
            textAlign: "center",
            marginBottom: 32,
            fontWeight: 700,
            letterSpacing: 1,
          }}
        >
          New Arrivals
        </Title>
        {loadingNewArrival ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Row gutter={[24, 24]} justify="center">
            {newArrivalSliderData.slice(0, 8).map((item, idx) => (
              <Col
                key={item.ProductID || idx}
                xs={24}
                sm={12}
                md={6}
                style={{ marginBottom: 24 }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: 320,
                    borderRadius: 8,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #f0f0f0",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    navigate(`/newarrival/${encodeURIComponent(item.Category)}`)
                  }
                >
                  <img
                    src={item.ImageUrl}
                    alt={item.ProductName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      bottom: 0,
                      width: "100%",
                      background: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      padding: "12px 16px 10px 12px",
                      fontWeight: 600,
                      fontSize: 16,
                      borderBottomLeftRadius: 8,
                      borderBottomRightRadius: 8,
                      textAlign: "left",
                      letterSpacing: 0.5,
                    }}
                  >
                    {item.ProductName}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Scroll to Top Floating Button */}
      <FloatButton.BackTop visibilityHeight={200} />
    </div>
  );
};

export default Home;
